import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { ConfirmModal } from '../components/ConfirmModal';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface TimetableVersion {
  id: string; school_id: string; school_year_id: string;
  school_year_name: string | null; term: string; status: string;
  conflict_count: number; published_at: string | null;
  effective_from: string | null; effective_to: string | null;
  created_at: string; slot_count: string; cloned_from: string | null;
}

const BN = {
  pageTitle: 'Versi Jadual Waktu',
  pageSubtitle: 'versi',
  backLink: '← Laman Utama',
  createBtn: '+ Versi Baharu',
  createFormTitle: 'Cipta Versi Jadual Baharu',
  labelTerm: 'Penggal *',
  labelEffectiveFrom: 'Tarikh Mula',
  labelEffectiveTo: 'Tarikh Tamat',
  termPlaceholder: 'cth: T1, T2, SEM1',
  confirmBtn: 'Cipta',
  confirmingBtn: 'Mencipta…',
  cancelBtn: 'Batal',
  cloneFormTitle: 'Salin Versi ke Penggal Baharu',
  cloneTermLabel: 'Penggal Baharu *',
  cloneTermPlaceholder: 'cth: T2, SEM2',
  cloneBtn: 'Salin',
  cloningBtn: 'Menyalin…',
  retryBtn: 'Cuba Lagi',
  emptyTitle: 'Tiada Versi Jadual',
  emptySubtitle: 'Belum ada versi jadual. Cipta yang pertama di atas.',
  viewGridBtn: '🔍 Lihat Grid',
  checkConflictsBtn: '🔎 Semak Konflik',
  checkingBtn: 'Memeriksa…',
  publishBtn: '📢 Terbitkan',
  cloneActionBtn: '📋 Salin',
  conflictLabel: 'konflik',
  readyToPublishLabel: '✓ Sedia terbit',
  clonedLabel: '📋 Salinan',
  publishedAtLabel: 'Diterbitkan',
  statusLabels: {
    DRAFT: 'Draf',
    PUBLISHED: 'Diterbitkan',
    ARCHIVED: 'Arkib',
  },
};

const EN = {
  pageTitle: 'Timetable Versions',
  pageSubtitle: 'versions',
  backLink: '← Home',
  createBtn: '+ New Version',
  createFormTitle: 'Create New Timetable Version',
  labelTerm: 'Term *',
  labelEffectiveFrom: 'Effective From',
  labelEffectiveTo: 'Effective To',
  termPlaceholder: 'e.g. T1, T2, SEM1',
  confirmBtn: 'Create',
  confirmingBtn: 'Creating…',
  cancelBtn: 'Cancel',
  cloneFormTitle: 'Clone Version to New Term',
  cloneTermLabel: 'New Term *',
  cloneTermPlaceholder: 'e.g. T2, SEM2',
  cloneBtn: 'Clone',
  cloningBtn: 'Cloning…',
  retryBtn: 'Retry',
  emptyTitle: 'No Timetable Versions',
  emptySubtitle: 'No timetable versions yet. Create the first one above.',
  viewGridBtn: '🔍 View Grid',
  checkConflictsBtn: '🔎 Check Conflicts',
  checkingBtn: 'Checking…',
  publishBtn: '📢 Publish',
  cloneActionBtn: '📋 Clone',
  conflictLabel: 'conflicts',
  readyToPublishLabel: '✓ Ready to publish',
  clonedLabel: '📋 Cloned',
  publishedAtLabel: 'Published',
  statusLabels: {
    DRAFT: 'Draft',
    PUBLISHED: 'Published',
    ARCHIVED: 'Archived',
  },
};

const STATUS_COLORS: Record<string, string> = {
  DRAFT: '#60a5fa', PUBLISHED: '#4ade80', ARCHIVED: '#9ca3af',
};

function getSchoolId(user: AuthUser): string | null {
  if (user.roles.some((r) => r.role === 'super_admin' && !r.schoolId)) return null;
  return user.roles.find((r) => r.schoolId)?.schoolId ?? null;
}

const DEMO_SCHOOL_ID    = '3f868b12-b554-4f51-a893-69e9b4ba9f57';
const DEMO_SCH_YEAR_ID  = '00000000-0000-0000-0000-000000000001';

export function TimetableVersionsPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;

  const navigate = useNavigate();
  const schoolId = getSchoolId(user) ?? DEMO_SCHOOL_ID;

  const STATUS_CFG = Object.fromEntries(
    Object.entries(UI.statusLabels).map(([k, label]) => [k, { label, color: STATUS_COLORS[k] ?? '#9ca3af' }])
  );

  const [versions, setVersions] = useState<TimetableVersion[]>([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [toast,    setToast]    = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [showCreate,  setShowCreate]  = useState(false);
  const [newForm,     setNewForm]     = useState({ term: '', effectiveFrom: '', effectiveTo: '' });
  const [creating,    setCreating]    = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const [cloneId,     setCloneId]    = useState<string | null>(null);
  const [cloneTerm,   setCloneTerm]  = useState('');
  const [cloning,     setCloning]    = useState(false);

  const [publishId,   setPublishId]  = useState<string | null>(null);
  const [publishing,  setPublishing] = useState(false);

  const [checkingId,  setCheckingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setVersions(await apiGet<TimetableVersion[]>(`/api/timetable/versions/${schoolId}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan versi jadual.');
    } finally { setLoading(false); }
  }, [schoolId]);

  useEffect(() => { void load(); }, [load]);

  async function handleCreate() {
    if (!newForm.term.trim()) { setCreateError('Sila masukkan penggal (cth: T1, T2). / Enter term (e.g. T1, T2).'); return; }
    if (newForm.effectiveTo && newForm.effectiveFrom && newForm.effectiveTo < newForm.effectiveFrom) {
      setCreateError('Tarikh tamat mesti selepas tarikh mula. / End date must be after start date.'); return;
    }
    setCreating(true); setCreateError(null);
    try {
      await apiPost<{ id: string }>('/api/timetable/versions', {
        schoolId, schoolYearId: DEMO_SCH_YEAR_ID,
        term:          newForm.term.trim().toUpperCase(),
        effectiveFrom: newForm.effectiveFrom || undefined,
        effectiveTo:   newForm.effectiveTo   || undefined,
      });
      setToast({ variant: 'success', message: 'Versi jadual baharu dicipta. / New timetable version created.' });
      setShowCreate(false);
      setNewForm({ term: '', effectiveFrom: '', effectiveTo: '' });
      void load();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : 'Gagal mencipta versi. / Failed to create version.');
    } finally { setCreating(false); }
  }

  async function handleCheck(id: string) {
    setCheckingId(id);
    try {
      const result = await apiPost<{ count: number; conflicts: unknown[] }>(`/api/timetable/versions/${id}/check`, {});
      if (result.count === 0) {
        setToast({ variant: 'success', message: 'Tiada konflik ditemui. Sedia untuk diterbitkan. / No conflicts found. Ready to publish.' });
      } else {
        setToast({ variant: 'error', message: `${result.count} konflik ditemui. Selesaikan sebelum menerbitkan. / ${result.count} conflict(s) found. Resolve before publishing.` });
      }
      void load();
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal memeriksa konflik.' });
    } finally { setCheckingId(null); }
  }

  async function handlePublish() {
    if (!publishId) return;
    setPublishing(true);
    try {
      await apiPost(`/api/timetable/versions/${publishId}/publish`, {});
      setToast({ variant: 'success', message: 'Jadual berjaya diterbitkan! / Timetable published successfully!' });
      setPublishId(null);
      void load();
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal menerbitkan jadual.' });
      setPublishId(null);
    } finally { setPublishing(false); }
  }

  async function handleClone() {
    if (!cloneId || !cloneTerm.trim()) {
      setToast({ variant: 'error', message: 'Sila masukkan penggal baharu. / Enter a new term.' }); return;
    }
    setCloning(true);
    try {
      const result = await apiGet<{ id: string }>(`/api/timetable/versions/${cloneId}/clone?term=${encodeURIComponent(cloneTerm.trim().toUpperCase())}`);
      setToast({ variant: 'success', message: `Versi disalin. / Version cloned.` });
      setCloneId(null); setCloneTerm('');
      void load();
      navigate(`/admin/timetable/${result.id}`);
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal menyalin. / Failed to clone.' });
    } finally { setCloning(false); }
  }

  const isAdmin = user.roles.some((r) => ['admin', 'principal', 'super_admin'].includes(r.role));

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{UI.backLink}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                🗓️ {UI.pageTitle}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {versions.length} {UI.pageSubtitle}
              </p>
            </div>
            {isAdmin && !showCreate && (
              <Button variant="primary" size="sm" onClick={() => { setShowCreate(true); setCreateError(null); }}>
                {UI.createBtn}
              </Button>
            )}
          </div>

          {/* Create form */}
          {showCreate && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg, boxShadow: shadows.xl }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                {UI.createFormTitle}
              </p>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                <div style={{ flex: '1 1 100px' }}>
                  <label style={labelSt}>{UI.labelTerm}</label>
                  <input type="text" value={newForm.term}
                    onChange={(e) => setNewForm((f) => ({ ...f, term: e.target.value }))}
                    placeholder={UI.termPlaceholder}
                    maxLength={10} disabled={creating} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={labelSt}>{UI.labelEffectiveFrom}</label>
                  <input type="date" value={newForm.effectiveFrom}
                    onChange={(e) => setNewForm((f) => ({ ...f, effectiveFrom: e.target.value }))}
                    disabled={creating} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 140px' }}>
                  <label style={labelSt}>{UI.labelEffectiveTo}</label>
                  <input type="date" value={newForm.effectiveTo}
                    min={newForm.effectiveFrom || undefined}
                    onChange={(e) => setNewForm((f) => ({ ...f, effectiveTo: e.target.value }))}
                    disabled={creating} style={inputSt()} />
                </div>
              </div>
              {createError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{createError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button variant="primary" size="sm" onClick={() => void handleCreate()} disabled={creating} loading={creating}>
                  {creating ? UI.confirmingBtn : UI.confirmBtn}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowCreate(false); setCreateError(null); }} disabled={creating}>{UI.cancelBtn}</Button>
              </div>
            </div>
          )}

          {/* Clone form */}
          {cloneId && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.lg, marginBottom: spacing.lg, border: `1px solid ${colors.primary.yellow}44` }}>
              <p style={{ margin: `0 0 ${spacing.sm}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                {UI.cloneFormTitle}
              </p>
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 120px' }}>
                  <label style={labelSt}>{UI.cloneTermLabel}</label>
                  <input type="text" value={cloneTerm}
                    onChange={(e) => setCloneTerm(e.target.value)}
                    placeholder={UI.cloneTermPlaceholder} maxLength={10} disabled={cloning} style={inputSt()} />
                </div>
                <Button variant="primary" size="sm" onClick={() => void handleClone()} disabled={cloning || !cloneTerm.trim()} loading={cloning}>
                  {cloning ? UI.cloningBtn : UI.cloneBtn}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setCloneId(null); setCloneTerm(''); }} disabled={cloning}>{UI.cancelBtn}</Button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
              <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{error}</p>
              <Button variant="ghost" size="sm" onClick={() => void load()} style={{ marginTop: spacing.xs }}>{UI.retryBtn}</Button>
            </div>
          )}

          {loading && versions.length === 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>
          )}

          {!loading && !error && versions.length === 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>🗓️</div>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>{UI.emptyTitle}</p>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {UI.emptySubtitle}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
            {versions.map((v) => {
              const sc         = STATUS_CFG[v.status] ?? { label: v.status, color: '#9ca3af' };
              const hasConflict = v.conflict_count > 0;
              const isChecking  = checkingId === v.id;

              return (
                <div key={v.id} style={{
                  backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                  border: `1px solid ${hasConflict ? 'rgba(248,113,113,0.4)' : v.status === 'PUBLISHED' ? 'rgba(74,222,128,0.3)' : colors.neutral.gray800}`,
                  overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  {/* Header */}
                  <div style={{ padding: spacing.lg, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: spacing.md, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ padding: '2px 10px', backgroundColor: `${sc.color}22`, color: sc.color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
                          {sc.label}
                        </span>
                        <span style={{ padding: '2px 10px', backgroundColor: 'rgba(255,255,255,0.06)', color: colors.neutral.white, borderRadius: borderRadius.full, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold }}>
                          {v.term}
                        </span>
                        {v.school_year_name && (
                          <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{v.school_year_name}</span>
                        )}
                        {v.cloned_from && (
                          <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>{UI.clonedLabel}</span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {Number(v.slot_count)} slot
                        {v.effective_from ? ` · ${new Date(v.effective_from).toLocaleDateString('ms-BN')}` : ''}
                        {v.effective_to   ? ` – ${new Date(v.effective_to).toLocaleDateString('ms-BN')}` : ''}
                        {v.published_at   ? ` · ${UI.publishedAtLabel} ${new Date(v.published_at).toLocaleDateString('ms-BN')}` : ''}
                      </p>
                    </div>

                    {/* Conflict badge */}
                    {hasConflict && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: spacing.xs, backgroundColor: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: borderRadius.lg, padding: `${spacing.xs} ${spacing.sm}` }}>
                        <span style={{ color: '#f87171', fontWeight: typography.fontWeight.bold, fontSize: typography.fontSize.sm }}>⚠ {v.conflict_count}</span>
                        <span style={{ color: '#f87171', fontSize: typography.fontSize.xs }}>{UI.conflictLabel}</span>
                      </div>
                    )}
                    {!hasConflict && v.status === 'DRAFT' && Number(v.slot_count) > 0 && (
                      <span style={{ color: '#4ade80', fontSize: typography.fontSize.xs }}>{UI.readyToPublishLabel}</span>
                    )}
                  </div>

                  {/* Actions */}
                  {v.status !== 'ARCHIVED' && (
                    <div style={{ padding: `${spacing.sm} ${spacing.lg}`, borderTop: `1px solid ${colors.neutral.gray800}`, display: 'flex', gap: spacing.sm, flexWrap: 'wrap', alignItems: 'center' }}>
                      <Button variant="secondary" size="sm" onClick={() => navigate(`/admin/timetable/${v.id}`)}>
                        {UI.viewGridBtn}
                      </Button>
                      {isAdmin && v.status === 'DRAFT' && (
                        <>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => void handleCheck(v.id)}
                            disabled={!!checkingId}
                            loading={isChecking}
                          >
                            {isChecking ? UI.checkingBtn : UI.checkConflictsBtn}
                          </Button>
                          <Button
                            variant={hasConflict ? 'ghost' : 'primary'} size="sm"
                            disabled={hasConflict || !!checkingId}
                            onClick={() => setPublishId(v.id)}
                          >
                            {UI.publishBtn}
                          </Button>
                        </>
                      )}
                      {isAdmin && (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => { setCloneId(v.id); setCloneTerm(''); }}
                          disabled={cloning}
                        >
                          {UI.cloneActionBtn}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Publish confirmation */}
      <ConfirmModal
        open={!!publishId}
        loading={publishing}
        onConfirm={() => void handlePublish()}
        onCancel={() => setPublishId(null)}
      />

      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
